import { Injectable, DOCUMENT, inject } from "@angular/core";
import { interval } from "rxjs";
import { Observable } from "rxjs/internal/Observable";
import { distinctUntilChanged, map } from "rxjs/operators";
import { Nullable } from "src/app/utils/utils";
import { Log } from "../utils/log";

export enum Cookies {
  ActivateDevPanel = "activateDevPanel",
  StbId = "stb_id",
  RebrandingContractsModalShown = "rebrandingContractsModalShown",
  CampaignModalShown = "campaignModalShown",
  KronAgreementsShown = "kronAgreementsShown",
  SurveyTrigger = "surveyTrigger",
}

export type CookieValue = Nullable<string | number | boolean>;

const COOKIE_WATCH_INTERVAL = 1000;

@Injectable({
  providedIn: "root",
})
export class CookieService {
  private readonly document = inject<Document>(DOCUMENT);

  /**
   * Be aware of the max-age property of these cookies!
   * @param name - The name of the cookie to set
   * @param value - The value of the cookie to set
   * @param maxAge - The maximum age of the cookie in seconds (default is 300 seconds)
   * @param domain - Optional cookie domain, e.g. ".storebrand.no". Required when the
   *                 cookie must be visible to other hosts under the same parent domain.
   */
  public setCookie(name: string, value: CookieValue, maxAge = 300, domain?: string): void {
    try {
      const isSecureContext = this.document.location.protocol === "https:";
      const isLocalhost = this.document.location.hostname === "localhost";
      const domainAttribute = domain ? `;domain=${domain}` : "";

      const baseCookieString = `${name}=${value};max-age=${maxAge};path=/${domainAttribute}`;

      // Safari-compatible cookie attributes
      const sameSiteAttributes = (() => {
        if (isSecureContext && !isLocalhost) {
          // Production HTTPS: Use SameSite=None;Secure for cross-site compatibility
          return ";SameSite=None;Secure";
        } else {
          // Local development or HTTP contexts: Use SameSite=Lax (Safari friendly)
          return ";SameSite=Lax";
        }
      })();

      this.document.cookie = baseCookieString + sameSiteAttributes;
    } catch (error) {
      Log.debug(`Failed to set ${name} cookie: `, error);
    }
  }

  public getCookie(name: string): CookieValue {
    return this.document.cookie
      .split(";")
      .map((s) => s.trim())
      .map((cookie) => cookie.split("="))
      .find(([key]) => key === name)?.[1];
  }

  public deleteCookie(name: string, domain?: string): void {
    try {
      const isSecureContext = this.document.location.protocol === "https:";
      const isLocalhost = this.document.location.hostname === "localhost";
      const domainAttribute = domain ? `;domain=${domain}` : "";

      const baseCookieString = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domainAttribute}`;

      // Use same Safari-compatible attributes as setCookie
      const sameSiteAttributes = isSecureContext && !isLocalhost ? ";SameSite=None;Secure" : ";SameSite=Lax";

      this.document.cookie = baseCookieString + sameSiteAttributes;
    } catch (error) {
      Log.debug(`Failed to delete ${name} cookie: `, error);
    }
  }

  public watch$(name: string): Observable<CookieValue | undefined> {
    return interval(COOKIE_WATCH_INTERVAL).pipe(
      map(() => this.document.cookie),
      distinctUntilChanged(),
      map((cookies) =>
        cookies
          .split(";")
          .map((s) => s.trim())
          .map((cookie) => cookie.split("="))
          .find(([key]) => key === name),
      ),
      map((value) => value?.[1]),
    );
  }

  public getOrCreateStbId(): string {
    const stbId = this.getCookie(Cookies.StbId);
    return stbId ? String(stbId) : this.generateStbIdAndSetCookie();
  }

  private generateStbIdAndSetCookie(): string {
    const stbId = this.generateStbId();
    const twoYearsInSeconds = 60 * 60 * 24 * 365 * 2;
    this.setCookie(Cookies.StbId, stbId, twoYearsInSeconds);
    return stbId;
  }

  private generateStbId(): string {
    return "xxxxxxxx-xxxx-8xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;

      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16).toUpperCase();
    });
  }
}
